/**
 * SEO Utilities for Dynamic Meta Tags
 *
 * This module provides utilities to dynamically update SEO meta tags
 * for Single Page Applications (SPAs) to ensure proper Google indexing.
 */

/**
 * Update the canonical URL for the current page
 * @param {string} url - The canonical URL (e.g., 'https://planlens.ai/pricing')
 */
export const updateCanonicalTag = (url) => {
  // Remove existing canonical tag if present
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.remove();
  }

  // Create and append new canonical tag
  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', url);
  document.head.appendChild(link);
};

/**
 * Update the page title
 * @param {string} title - The page title
 */
export const updatePageTitle = (title) => {
  document.title = title;
};

/**
 * Update meta description
 * @param {string} description - The page description
 */
export const updateMetaDescription = (description) => {
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', description);
};

/**
 * Update Open Graph meta tags
 * @param {Object} og - Open Graph properties
 * @param {string} og.title - OG title
 * @param {string} og.description - OG description
 * @param {string} og.url - OG URL
 * @param {string} og.image - OG image URL
 */
export const updateOpenGraphTags = ({ title, description, url, image }) => {
  const updateOGTag = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  if (title) updateOGTag('og:title', title);
  if (description) updateOGTag('og:description', description);
  if (url) updateOGTag('og:url', url);
  if (image) updateOGTag('og:image', image);
};

/**
 * Update Twitter Card meta tags
 * @param {Object} twitter - Twitter Card properties
 * @param {string} twitter.title - Twitter title
 * @param {string} twitter.description - Twitter description
 * @param {string} twitter.url - Twitter URL
 * @param {string} twitter.image - Twitter image URL
 */
export const updateTwitterTags = ({ title, description, url, image }) => {
  const updateTwitterTag = (name, content) => {
    let meta = document.querySelector(`meta[name="${name}"]`) ||
               document.querySelector(`meta[property="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  if (title) updateTwitterTag('twitter:title', title);
  if (description) updateTwitterTag('twitter:description', description);
  if (url) updateTwitterTag('twitter:url', url);
  if (image) updateTwitterTag('twitter:image', image);
};

/**
 * Update all SEO tags for a page
 * @param {Object} seo - SEO configuration
 * @param {string} seo.title - Page title
 * @param {string} seo.description - Page description
 * @param {string} seo.canonical - Canonical URL
 * @param {string} seo.image - Social media image URL (optional)
 */
export const updatePageSEO = ({ title, description, canonical, image }) => {
  if (title) updatePageTitle(title);
  if (description) updateMetaDescription(description);
  if (canonical) updateCanonicalTag(canonical);

  // Update Open Graph tags
  updateOpenGraphTags({
    title,
    description,
    url: canonical,
    image: image || 'https://planlens.ai/og-image.png'
  });

  // Update Twitter Card tags
  updateTwitterTags({
    title,
    description,
    url: canonical,
    image: image || 'https://planlens.ai/twitter-card.png'
  });
};

/**
 * Hook to update SEO on page load
 * Use this in useEffect in your React components
 *
 * @example
 * useEffect(() => {
 *   updatePageSEO({
 *     title: 'Pricing - PlanLens.ai',
 *     description: 'Affordable AI-powered financial planning...',
 *     canonical: 'https://planlens.ai/pricing'
 *   });
 * }, []);
 */
export default updatePageSEO;
